import React from 'react';
import { Link } from 'react-router-dom';
import { get } from 'lodash';
import Spinner from '../spinner';
import { LeaderboardListItem } from '../LeaderboardListItem';
import type { LeaderboardItemConfig } from '../LeaderboardListItem';
import type { Fundraiser } from '../../../types/leaderboard';
import styles from './FundraiserList.module.css';

interface FundraiserListProps {
  fundraisers: Fundraiser[];
  maxItems?: number;
  viewAllLink?: string;
  viewAllText?: string;
  emptyStateMessage?: string;
  showRank?: boolean;
  onFundraiserClick?: (fundraiser: Fundraiser) => void;
  isLoading?: boolean;
}

export const FundraiserList: React.FC<FundraiserListProps> = ({
  fundraisers,
  maxItems = 10,
  viewAllLink,
  viewAllText = 'View all',
  emptyStateMessage = 'No fundraisers found',
  showRank = true,
  onFundraiserClick,
  isLoading = false,
}) => {
  const displayedFundraisers = fundraisers.slice(0, maxItems);

  // Configuration for fundraiser items
  const fundraiserConfig: LeaderboardItemConfig<Fundraiser> = {
    getId: (f) => f.id,
    getName: (f) => {
      const name = f.name || '';
      const firstName = get(f, 'account.firstName', '');
      const lastName = get(f, 'account.lastName', '');
      const fullName = `${firstName} ${lastName}`.trim();
      return name || fullName;
    },
    getAmount: (f) => Math.floor(Number(f.raisedAmount || 0)),
    getMetadata: (f) => {
      const count = f.donationCount || 0;
      return `${count} donation${count !== 1 ? 's' : ''}`;
    },
    getLink: (f) => {
      switch (f.type) {
        case 'team':
          return `/fundraisers/team/view/${f.team?.id}`;
        case 'collection':
          return `/fundraisers/collection/view/${f.collection?.id}`;
        case 'fundraiser':
        default:
          return `/fundraisers/view/${f.fundraiser?.id}`;
      }
    },
    getCurrency: (f) => f.currency,
    getImageUrl: (f) => {
      const typeImage = f[f.type]?.mainImageUrl;
      const campaignImage = f.campaign?.mainImageUrl;
      const orgImage = f.organization?.mainImageUrl;
      return typeImage ?? campaignImage ?? orgImage ?? null;
    },
    showAvatar: true,
    fullRowClickable: true,
  };

  return (
    <div className={styles.leaderboardList}>
      {isLoading ? (
        <div className={styles.emptyState}>
          <Spinner />
        </div>
      ) : !fundraisers.length ? (
        <div className={styles.emptyState}>
          <p>{emptyStateMessage}</p>
        </div>
      ) : (
        <>
          {displayedFundraisers.map((fundraiser, index) => (
            <LeaderboardListItem
              key={fundraiser.id || index}
              item={fundraiser}
              rank={index + 1}
              showRank={showRank}
              config={fundraiserConfig}
              onClick={onFundraiserClick}
            />
          ))}
          {viewAllLink && (
            <Link to={viewAllLink} className={styles.viewAllButton}>
              {viewAllText}
            </Link>
          )}
        </>
      )}
    </div>
  );
};
